import Spinner from '@legacy/core/components/Spinner';
import { Component } from "react";
import deepcopy from "rfdc";
import { PriceBookItemTypes } from '../core/utils/enums';
import { historyHasState, sendDataToServer } from "../core/utils/utils";
import VendorCreditDetailsCard from "./components/VendorCreditDetailsCard";
import VendorCreditVoidForm from "./forms/VendorCreditVoidForm";

const PAGE_MODES = {
    VIEW_VENDOR_CREDIT: "VIEW_VENDOR_CREDIT",
    VOID_VENDOR_CREDIT: "VOID_VENDOR_CREDIT",
}

const PAGE_MODE_SUBTITLES = {
    VIEW_VENDOR_CREDIT: "Vendor Credit Details",
    VOID_VENDOR_CREDIT: "Void Vendor Credit",
}

const PAGE_MODE_BACK_BUTTON_DISPLAY = {
    VIEW_VENDOR_CREDIT: "flex",
    VOID_VENDOR_CREDIT: "none",
}

const PRIMARY_PAGE_MODES = [PAGE_MODES.VIEW_VENDOR_CREDIT]
const SECONDARY_PAGE_MODES = [
    PAGE_MODES.VOID_VENDOR_CREDIT,
]

const FORM_DATA_NAMES_BY_MODE = {
    VIEW_VENDOR_CREDIT: "vendorCreditData",
    VOID_VENDOR_CREDIT: "voidData",
}

const SUBMITTING_NAMES_BY_MODE = {
    VIEW_VENDOR_CREDIT: "submittingVendorCredit",
    VOID_VENDOR_CREDIT: "submittingVoid",
}

const ERROR_NAMES_BY_MODE = {
    VIEW_VENDOR_CREDIT: "vendorCredit",
    VOID_VENDOR_CREDIT: "void",
}


class VendorCreditDetailsContainer extends Component {

    // Initialize

    constructor(props) {
        super(props)

        const defaultMode = this.props.formMode || PAGE_MODES.VIEW_VENDOR_CREDIT

        this.addToastToQueue = this.props.addToastToQueue

        this.state = {
            vendorCreditData: null,

            voidData: {},

            errors: {
                vendorCredit: {},
                void: {},
            },

            defaultMode: defaultMode,
            mode: defaultMode,

            inventoryReturned: false,

            returnScroll: 0,
        }

        window.onpopstate = (event) => {
            if (event.state !== null && Object.keys(event.state).length) {
                this.setState(event.state)
            }
        }
    }

    componentDidMount = async () => {
        if (this.state.vendorCreditData === null) {
            const vendorCreditEndpoint = DjangoUrls["bills:api-vendor-credits-detail"](window.MARKETPLACE_ENTITY_SLUG, window.VENDOR_CREDIT_ID)
            const vendorCreditResponse = await fetch(vendorCreditEndpoint)
            const vendorCredit = await vendorCreditResponse.json()

            this.setState((state, props) => {
                let updatedState = state
                updatedState.vendorCreditData = vendorCredit

                // Split line items into their respective lists
                updatedState.vendorCreditData.service_charges = vendorCredit.line_items.filter(lineItem => lineItem.line_item_type === PriceBookItemTypes.service)
                updatedState.vendorCreditData.parts = vendorCredit.line_items.filter(lineItem => lineItem.line_item_type === PriceBookItemTypes.part)
                updatedState.vendorCreditData.other_charges = vendorCredit.line_items.filter(lineItem => lineItem.line_item_type === PriceBookItemTypes.other)

                updatedState.inventoryReturned = vendorCredit.line_items.some(lineItem => lineItem.remove_from_inventory === true)

                return updatedState
            })
        }

        if (historyHasState(history)) {
            if (document.querySelector(".page-subtitle")) {
                document.querySelector(".page-subtitle").innerHTML = PAGE_MODE_SUBTITLES[history.state.mode]
            }
            if (document.querySelector(".back-button")) {
                document.querySelector(".back-button").style.display = PAGE_MODE_BACK_BUTTON_DISPLAY[history.state.mode]
            }
            this.setState(history.state)
        }
    }

    // Form helpers

    updateFormData = (formName, fieldName, fieldValue, then=null) => {
        this.setState((state, props) => {
            let updatedState = state
            updatedState[formName][fieldName] = fieldValue
            return updatedState
        }, then)
    }

    switchFormMode = (mode) => {
        if (document.querySelector(".page-subtitle")) {
            document.querySelector(".page-subtitle").innerHTML = PAGE_MODE_SUBTITLES[mode]
        }
        if (document.querySelector(".back-button")) {
            document.querySelector(".back-button").style.display = PAGE_MODE_BACK_BUTTON_DISPLAY[mode]
        }

        if (SECONDARY_PAGE_MODES.includes(mode)) {
            history.replaceState(this.state, "", "")
        }

        this.setState((state, props) => {
            let updatedState = state
            updatedState.mode = mode
            history.pushState(updatedState, "", "?mode=" + mode.toLowerCase().replace(/_/g, "-"));
            return updatedState
        })
    }

    switchToPrimaryForm = () => {
        this.setState((state, props) => {
            let updatedState = state

            // Clear the secondary form data
            updatedState[FORM_DATA_NAMES_BY_MODE[state.mode]] = {}
            updatedState[SUBMITTING_NAMES_BY_MODE[state.mode]] = false
            updatedState.errors[ERROR_NAMES_BY_MODE[state.mode]] = {}

            return updatedState
        })
        this.switchFormMode(this.state.defaultMode)
    }

    switchToSecondaryForm = (newFormMode, data, initialData) => {
        this.setState((state, props) => {
            let updatedState = state
            // Set the scroll state
            updatedState.returnScroll = document.querySelector(".main").scrollTop

            updatedState[FORM_DATA_NAMES_BY_MODE[newFormMode]] = {}

            if (data !== null) {
                updatedState[FORM_DATA_NAMES_BY_MODE[newFormMode]] = deepcopy()(data)
            }

            if (initialData !== null) {
                Object.assign(updatedState[FORM_DATA_NAMES_BY_MODE[newFormMode]], initialData)
            }

            return updatedState
        })

        this.switchFormMode(newFormMode)
    }

    // Create vendor credit (finalize from draft)

    createVendorCredit = async () => {
        const dataName = "vendorCreditData"
        const submittingName = "submittingVendorCredit"
        const errorDictName = "vendorCredit"
        const endpoint = DjangoUrls["bills:api-vendor-credits-list"](window.MARKETPLACE_ENTITY_SLUG)
        const endpointMethod = "POST"

        const dataManipulator = (data, state) => {
            let preparedData = deepcopy()(data)
            preparedData.is_draft = false

            // Convert bill to ID
            if (data.bill !== null) {
                preparedData.bill = data.bill.id
            }

            // Convert service company to slug
            preparedData.service_company = data.service_company.slug

            // Convert vendor to ID
            preparedData.vendor = data.vendor.id

            return preparedData
        }

        const onSuccess = (vendorCredit) => {
            const successUrl = DjangoUrls["bills:vendor-credits-detail"](window.MARKETPLACE_ENTITY_SLUG, vendorCredit.id)
            this.addToastToQueue({
                type: "success",
                variant: "default",
                title: `Vendor Credit "${vendorCredit.custom_id || vendorCredit.id}" created`,
                path: successUrl.split("?")[0],
                barePathOnly: true,
                delayRender: true,
            })

            history.replaceState({}, "", "")
            location.assign(successUrl)
        }
        const onError = () => {
            this.addToastToQueue({
                type: "error",
                variant: "default",
                title: "Vendor Credit could not be created",
            })
        }

        await sendDataToServer(this, endpoint, endpointMethod, dataName, submittingName, errorDictName, onSuccess, onError, dataManipulator, undefined)
    }

    // Void vendor credit

    voidVendorCredit = async () => {
        const endpoint = DjangoUrls["bills:api-vendor-credits-void"](window.MARKETPLACE_ENTITY_SLUG, this.state.vendorCreditData.id)
        const endpointMethod = "POST"
        const successUrl = DjangoUrls["bills:vendor-credits-detail"](window.MARKETPLACE_ENTITY_SLUG, this.state.vendorCreditData.id)

        const dataName = "voidData"
        const submittingName = "submittingVoid"
        const errorDictName = "void"

        const onSuccess = (json) => {
            this.addToastToQueue({
                type: "success",
                variant: "default",
                title: `Vendor Credit "${this.state.vendorCreditData.custom_id || this.state.vendorCreditData.id}" voided`,
                path: successUrl.split("?")[0],
                barePathOnly: true,
                delayRender: true,
            })
            history.replaceState({}, "", "")
            location.assign(successUrl)
        }

        const onError = () => {
            this.addToastToQueue({
                type: "error",
                variant: "default",
                title: "Vendor Credit could not be voided",
            })
        }

        await sendDataToServer(this, endpoint, endpointMethod, dataName, submittingName, errorDictName, onSuccess, onError, undefined, undefined)
    }

    // Handle Actions

    handleActionRequest = (action) => {
        switch (action) {
            case "VENDOR_CREDIT_CREATE":
                this.createVendorCredit()
                break
            case "VENDOR_CREDIT_EDIT":
                location.assign(DjangoUrls["bills:vendor-credits-update"](window.MARKETPLACE_ENTITY_SLUG, this.state.vendorCreditData.id))
                break
            case "VENDOR_CREDIT_VOID":
                this.switchToSecondaryForm(PAGE_MODES.VOID_VENDOR_CREDIT, null, null)
                break
            case "VENDOR_CREDIT_VOID_SUBMIT":
                this.voidVendorCredit()
                break
            default:
                console.error(`No action handler exists for action "${action}".`)
        }
    }

    // Render

    render() {
        if (this.state.vendorCreditData === null) {
            return <Spinner centered={true} />
        }
        else {
            if (PRIMARY_PAGE_MODES.includes(this.state.mode)) {
                return <VendorCreditDetailsCard
                    vendorCredit={this.state.vendorCreditData}
                    requestAction={this.handleActionRequest}
                    switchToSecondaryForm={this.switchToSecondaryForm}
                    returnScroll={this.state.returnScroll}
                    submitting={this.state.submittingVendorCredit}
                    errors={this.state.errors.vendorCredit}
                ></VendorCreditDetailsCard>
            }
            else if (this.state.mode === PAGE_MODES.VOID_VENDOR_CREDIT) {
                return <VendorCreditVoidForm
                    vendorCredit={this.state.vendorCreditData}
                    voidData={this.state.voidData}
                    requestAction={this.handleActionRequest}
                    switchToPrimaryForm={this.switchToPrimaryForm}
                    submitting={this.state.submittingVoid}
                    errors={this.state.errors.void}
                    onFormDataChange={(fieldName, fieldValue) => this.updateFormData("voidData", fieldName, fieldValue)}
                    inventoryReturned={this.state.inventoryReturned}
                    returnScroll={0}
                ></VendorCreditVoidForm>
            }
            else {
                return (
                    <div className="data-panel-container data-panel-container--with-margin">
                        <div className="data-panel" aria-label="Unknown Form Mode">
                            <div className="data-panel__form">
                                <p className="data-panel__form__caption">An unhandled form mode was supplied.</p>
                            </div>
                        </div>
                    </div>
                )
            }
        }
    }
}

export default VendorCreditDetailsContainer;
